import Vue from "vue";
import { getSubModules, addInterceptor, vueStoreConfig, isStoreInstance } from "./utils";
const h = typeof window !== "undefined" && window.__VUE_DEVTOOLS_GLOBAL_HOOK__;
function isRootStore(store) {
    return !!store.getModules;
}
function prettySubModuleNames(subModules) {
    const result = {};
    Object.entries(subModules).forEach(([name, module]) => result[name.endsWith("Store") ? name.substring(0, name.length - 5) : name] = module);
    return result;
}
export default function devtoolPlugin(store, config) {
    if (!h)
        return;
    const wrapper = isStoreInstance(store) ? store : {
        $data: {},
        $options: { $vuestore: { submodules: prettySubModuleNames(isRootStore(store) ? store.getModules() : store), interceptors: [] } },
        $watch: function () { }
    };
    // tslint:disable-next-line:no-unused-expression
    const adapter = new StoreAdapter(wrapper, h, config);
    h.emit("vuex:init", adapter);
    h.on("vuex:travel-to-state", (targetState) => {
        adapter.replaceState(targetState);
    });
    adapter.interceptMutations((context) => h.emit("vuex:mutation", context, adapter));
}
class StoreAdapter {
    // private uid: string;
    constructor(store, hook, config = {}, rootAdapter) {
        this.store = store;
        this.config = config;
        this._committing = false;
        this.subModules = {};
        if (!vueStoreConfig(store))
            throw new Error("Store is not a valid vuestore class. Did you forget to add @Store decorator?");
        if (!this.config.separator)
            this.config.separator = "/";
        if (!this.config.prefix)
            this.config.prefix = vueStoreConfig(store).name;
        this.rootAdapter = rootAdapter || this;
        this.store.__VUE_DEVTOOLS_UID__ = `${this.config.prefix || 'root'}-${Date.now()}`;
        this.subModules = this.getSubAdapters(hook);
        if (this.config.strict)
            this.enableStrictMode();
    }
    _withCommit(fn) {
        const committing = this._committing;
        this._committing = true;
        fn();
        this._committing = committing;
    }
    enableStrictMode() {
        if (process.env.NODE_ENV === 'production')
            return;
        this.store.$watch(() => this.store.$data, () => assert(this.rootAdapter._committing, `Do not mutate vuestore outside mutation methods.`), { deep: true, sync: true });
        // Object.values(this.subModules).forEach(subModule => subModule.enableStrictMode());
    }
    addPrefix(name) {
        return this.config.prefix ? `${this.config.prefix}${this.config.separator}${name}` : name;
    }
    getSubAdapters(hook) {
        const sm = {};
        Object.entries(getSubModules(this.store)).forEach(([name, subModule]) => {
            sm[name] = new StoreAdapter(subModule, hook, { ...this.config, prefix: this.addPrefix(name) }, this.rootAdapter);
        });
        return sm;
    }
    interceptMutations(handler) {
        addInterceptor(this.store, (context) => {
            this.rootAdapter._withCommit(() => context.handler());
            handler({ type: this.addPrefix(context.name), payload: context.payload });
        }, false);
        Object.values(this.subModules).forEach(sm => sm.interceptMutations(handler));
    }
    /** Hacks para que no rompa DevTools 5 (no anda bien) */
    get _vm() {
        const store = this;
        return {
            $options: {
                data() {
                    return {
                        $$state: store.state
                    };
                },
                computed: store.getters
            }
        };
    }
    set _vm(_) {
    }
    get _mutations() {
        // const methods = Object.getOwnPropertyNames(this.store).filter(item => typeof this.store[item] === 'function');
        // console.log("%o", methods);
        return [];
    }
    /** Fin Hacks para que no rompa DevTools 5 (no anda bien) */
    get state() {
        const state = { ...this.store.$data };
        Object.entries(this.subModules).forEach(([name, subModule]) => {
            if (this.config.flat) {
                Object.keys(subModule.state).forEach(k => state[subModule.addPrefix(k)] = subModule.state[k]);
            }
            else {
                state[name] = subModule.state;
                // state[name] = {
                //   _custom: {
                //     type: 'component',
                //     id: (subModule.store as any).__VUE_DEVTOOLS_UID__,
                //     display: subModule.store.$options.name || (subModule.store.$options as any)._componentTag,
                //     value: subModule.state
                //   }
                // };
            }
        });
        return state;
    }
    get getters() {
        const getters = {};
        if (this.store.$options.computed) {
            Object.getOwnPropertyNames(this.store.$options.computed)
                .filter(name => !this.subModules[name])
                .forEach(name => {
                let value;
                try {
                    value = this.store[name];
                }
                catch (e) {
                    value = e.message;
                }
                getters[name] = value;
            });
        }
        const subGetters = {};
        Object.entries(this.subModules).forEach(([name, subModule]) => {
            const moduleGetters = subModule.getters;
            if (this.config.flat) {
                Object.keys(moduleGetters).forEach(k => subGetters[subModule.addPrefix(k)] = moduleGetters[k]);
            }
            else {
                subGetters[name] = {
                    _custom: {
                        type: 'component',
                        display: subModule.store.$options.name || subModule.store.$options._componentTag,
                        value: moduleGetters
                    }
                };
            }
        });
        return { ...getters, ...subGetters };
    }
    _resetModule() {
        this.rootAdapter._withCommit(() => {
            Object.values(this.subModules).forEach(m => m._resetModule());
            Object.keys(this.store.$data).forEach(n => {
                Vue.set(this.store.$data, n, undefined);
            });
        });
    }
    _replaceModuleState(state) {
        this.rootAdapter._withCommit(() => {
            Object.keys(state).forEach(n => {
                const prefix = n.split(this.config.separator)[0];
                const subModule = this.subModules[prefix];
                if (subModule) {
                    subModule._replaceModuleState(this.config.flat ? this.getLocalState(state, prefix) : state[n]);
                }
                else {
                    Vue.set(this.store.$data, n, state[n]);
                }
            });
        });
    }
    getLocalState(state, subModuleName) {
        const localState = {};
        const prefix = subModuleName + this.config.separator;
        Object.keys(state)
            .filter(k => k.startsWith(prefix))
            .forEach(k => localState[k.replace(prefix, "")] = state[k]);
        return localState;
    }
    replaceState(state) {
        this._resetModule();
        this._replaceModuleState(state);
    }
}
function assert(condition, msg) {
    if (!condition)
        throw new Error(`[vuestore] ${msg}`);
}
//# sourceMappingURL=devTools.js.map